import { useState, useRef, useEffect } from "react";
import { Video, ArrowLeft, MapPin, Smile, Meh, AlertTriangle, Users, Upload, Plus, X, VideoOff, Circle, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MentionTextArea } from "./MentionTextArea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cloudinaryService } from "@/services/cloudinaryService";
import { useCropPlan } from "@/hooks/useCropPlan";
import { usePosts } from "@/hooks/usePosts";
import { useToast } from "@/hooks/use-toast";
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { supabase } from "@/integrations/supabase/client";

interface CreatePostProps {
  onBack: () => void;
}

const sentimentOptions = [
  {
    id: "great",
    label: "Great",
    icon: Smile,
    color: "text-green-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    selectedBgColor: "bg-green-200",
    selectedBorderColor: "border-green-500 border-2",
    hoverBgColor: "hover:bg-green-100"
  },
  {
    id: "standard",
    label: "Standard",
    icon: Meh,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    selectedBgColor: "bg-blue-200",
    selectedBorderColor: "border-blue-500 border-2",
    hoverBgColor: "hover:bg-blue-100"
  },
  {
    id: "issues",
    label: "Issues",
    icon: AlertTriangle,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    selectedBgColor: "bg-orange-200",
    selectedBorderColor: "border-orange-500 border-2",
    hoverBgColor: "hover:bg-orange-100"
  },
  {
    id: "help",
    label: "Need Help",
    icon: Users,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    selectedBgColor: "bg-purple-200",
    selectedBorderColor: "border-purple-500 border-2",
    hoverBgColor: "hover:bg-purple-100"
  }
];

const predefinedChallenges = [
  { id: "soil", label: "Soil" },
  { id: "water", label: "Water" },
  { id: "pest-disease", label: "Pest/Disease" },
  { id: "climate-change", label: "Climate Change" },
  { id: "price", label: "Price" },
  { id: "labor", label: "Labor" },
  { id: "land-degradation", label: "Land Degradation" },
  { id: "farm-tech", label: "Farm Tech" },
  { id: "other", label: "Other" }
];

export const CreatePost = ({ onBack }: CreatePostProps) => {
  const { userCropPlans } = useCropPlan();
  const { addPost } = usePosts();
  const { toast } = useToast();
  const connectionMode = useConnectionMode();
  const isLiteMode = connectionMode === 'lite';
  
  // Lite mode restrictions for 2G/3G users
  const videoLimits = {
    maxSizeMB: isLiteMode ? 15 : 100, // 15MB for lite mode vs 100MB for full mode
    maxDurationSeconds: isLiteMode ? 30 : 300, // 30 seconds for lite mode vs 5 minutes for full mode
    maxResolution: isLiteMode ? 480 : 1080 // 480p for lite mode vs 1080p for full mode
  };
  const [content, setContent] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);
  const [customChallenges, setCustomChallenges] = useState<string[]>([]);
  const [newCustomChallenge, setNewCustomChallenge] = useState("");
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [sentiment, setSentiment] = useState<string>("");
  const [videoType, setVideoType] = useState<string>("");
  const [selectedCropPlan, setSelectedCropPlan] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPosting, setIsPosting] = useState(false);
  
  // Live video recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer] = useState<NodeJS.Timeout | null>(null);
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup function for camera stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
    };
  }, [stream, recordingTimer]);

  const startLiveRecording = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: isLiteMode ? 640 : 1280 },
          height: { ideal: isLiteMode ? 480 : 720 },
          facingMode: 'environment' // Prefer back camera for farming videos
        }, 
        audio: true 
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: isLiteMode ? 500000 : 1500000 // 500kbps for lite mode vs 1.5Mbps for full mode
      });
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };
      
      recorder.onstop = () => {
        handleRecordingComplete();
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer with lite mode duration limit
      const timer = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop recording if lite mode limit is reached
          if (isLiteMode && newTime >= videoLimits.maxDurationSeconds) {
            stopRecording();
            toast({
              title: "Recording stopped",
              description: `Reached ${videoLimits.maxDurationSeconds}s limit for 2G/3G networks`,
              variant: "default"
            });
          }
          return newTime;
        });
      }, 1000);
      
      toast({
        title: "Recording started",
        description: isLiteMode 
          ? `Recording for 2G/3G networks (max ${videoLimits.maxDurationSeconds}s)` 
          : "Share your farming progress!",
      });
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: "Camera access denied",
        description: "Please allow camera access to record videos",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    }
  };

  const handleRecordingComplete = async () => {
    if (recordedChunks.length === 0) return;
    
    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    setRecordedChunks([]);
    
    // Convert to File for upload
    const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
    
    // Upload the recorded video
    await uploadMediaFile(file);
  };

  // Helper function to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const uploadMediaFile = async (file: File) => {
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    // Validate file type
    if (!isVideo && !isImage) {
      toast({
        title: "Invalid file type",
        description: "Please select a video or image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size based on connection mode and media type
    const maxSizeMB = isLiteMode 
      ? (isVideo ? 15 : 5) // 15MB for videos, 5MB for images in lite mode
      : (isVideo ? 100 : 50); // 100MB for videos, 50MB for images in full mode
    
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: isLiteMode 
          ? `For 2G/3G networks, ${isVideo ? 'videos' : 'images'} must be smaller than ${maxSizeMB}MB to ensure fast uploads`
          : `${isVideo ? 'Video' : 'Image'} file must be smaller than ${maxSizeMB}MB`,
        variant: "destructive"
      });
      return;
    }

    // Check video duration for lite mode (only for videos)
    if (isVideo && isLiteMode) {
      try {
        const duration = await getVideoDuration(file);
        if (duration > videoLimits.maxDurationSeconds) {
          toast({
            title: "Video too long",
            description: `For 2G/3G networks, videos must be ${videoLimits.maxDurationSeconds} seconds or shorter for better loading`,
            variant: "destructive"
          });
          return;
        }
      } catch (error) {
        console.warn('Could not check video duration:', error);
      }
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get current user for folder structure
      const { data: { user } } = await supabase.auth.getUser();
      
      // Use video title for folder structure if provided, otherwise use content
      const postTitle = videoTitle.trim() || content.trim() || `${isVideo ? 'video' : 'image'}-post`;
      
      // Current date for folder structure
      const postDate = new Date().toISOString().split('T')[0];

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      // Upload with structured folder path
      const result = isVideo 
        ? await cloudinaryService.uploadVideo(file, {
            userId: user?.id,
            postTitle,
            postDate
          })
        : await cloudinaryService.uploadImage(file, {
            userId: user?.id,
            postTitle,
            postDate
          });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setSelectedMedia(result.secure_url);
        setMediaType(isVideo ? 'video' : 'image');
        setIsUploading(false);
        setUploadProgress(0);
        setShowVideoOptions(false);
        
        toast({
          title: `${isVideo ? 'Video' : 'Image'} uploaded successfully!`,
          description: isLiteMode && isVideo 
            ? `Video optimized for 2G/3G networks (360p, ${videoLimits.maxSizeMB}MB limit)`
            : `Organized in: ${cloudinaryService.getFolderInfo(user?.id || 'anonymous', postDate)}`,
        });
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: `Failed to upload ${isVideo ? 'video' : 'image'}. Please try again.`,
        variant: "destructive"
      });
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await uploadMediaFile(file);
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChallengeToggle = (challengeId: string) => {
    setSelectedChallenges(prev => 
      prev.includes(challengeId) 
        ? prev.filter(id => id !== challengeId)
        : [...prev, challengeId]
    );
  };

  const handleAddCustomChallenge = () => {
    if (newCustomChallenge.trim() && !customChallenges.includes(newCustomChallenge.trim())) {
      setCustomChallenges(prev => [...prev, newCustomChallenge.trim()]);
      setNewCustomChallenge("");
    }
  };

  const handleRemoveCustomChallenge = (challenge: string) => {
    setCustomChallenges(prev => prev.filter(c => c !== challenge));
  };

  const getAllChallenges = () => {
    const predefinedSelected = predefinedChallenges
      .filter(challenge => selectedChallenges.includes(challenge.id))
      .map(challenge => challenge.label);
    
    return [...predefinedSelected, ...customChallenges];
  };

  const handlePost = async () => {
    if (!selectedMedia || !sentiment || !videoType || !content.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields (video, content, sentiment, and video type)",
        variant: "destructive"
      });
      return;
    }

    setIsPosting(true);

    try {
      const selectedCrop = userCropPlans.find(crop => crop.id === selectedCropPlan);
      const allChallenges = getAllChallenges();
      
      const postData = {
        content: content.trim(),
        whats_happening: null,
        challenges: allChallenges.length > 0 ? allChallenges.join(", ") : null,
        image_url: selectedMedia,
        is_video: mediaType === 'video',
        sentiment: sentiment,
        crop_type: selectedCrop?.commodity || null,
        harvest_week_month: "Current harvest",
        category: selectedCrop?.group_name || "Produce",
        location_lat: null,
        location_lng: null,
        location_address: location || null,
        // Enhanced metadata for lite mode video optimization
        video_metadata: mediaType === 'video' ? {
          connection_mode: connectionMode,
          is_lite_optimized: isLiteMode,
          max_quality: isLiteMode ? '360p' : '720p',
          bitrate_cap: isLiteMode ? '500k' : '2000k',
          preload_setting: isLiteMode ? 'none' : 'metadata'
        } : null,
        crop_plan_info: selectedCrop && videoType === "crop-update" ? {
          variety: selectedCrop.variety,
          season: selectedCrop.season,
          year: selectedCrop.year,
          isPrimary: selectedCrop.is_primary,
          commodity: selectedCrop.commodity,
          groupName: selectedCrop.group_name,
          title: videoTitle || null
        } : { title: videoTitle || null }
      };

      console.log("📝 Creating post with data:", postData);

      await addPost(postData);

      toast({
        title: "Post created!",
        description: "Your video has been shared successfully",
      });

      onBack();
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: "Failed to create post",
        description: (error as any)?.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsPosting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const currentYearCrops = userCropPlans.filter(crop => crop.year === currentYear);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <h2 className="text-lg font-semibold">Share Your Progress</h2>
        <Button
          onClick={handlePost}
          disabled={!selectedMedia || !sentiment || !videoType || !content.trim() || isUploading || isPosting || isRecording}
          className="bg-green-600 hover:bg-green-700 text-white px-6"
        >
          {isPosting ? "Posting..." : "Post"}
        </Button>
      </div>

      <div className="p-4 space-y-6">
        {!selectedMedia && !isUploading && !isRecording && !showVideoOptions ? (
          <div className="bg-white rounded-xl p-4 text-center border border-gray-200">
            {isLiteMode && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Video className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">2G/3G Mode Active</span>
                </div>
                <p className="text-xs text-orange-700">
                  Videos limited to {videoLimits.maxDurationSeconds}s and {videoLimits.maxSizeMB}MB for faster uploads on slower networks
                </p>
              </div>
            )}
            
            <h3 className="text-md font-medium text-gray-700 mb-3">
              Add Image or Video
            </h3>
            
            <div className="flex gap-2 justify-center flex-wrap">
              {/* Image Upload */}
              <button
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.accept = "image/*";
                    fileInputRef.current.capture = "environment";
                    fileInputRef.current.click();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 text-sm"
              >
                <Upload className="text-blue-600" size={18} />
                <span className="text-blue-700 font-medium">Upload Image</span>
              </button>
              
              {/* Live Recording */}
              <button
                onClick={() => setShowVideoOptions(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200 text-sm"
              >
                <Circle className="text-red-600 fill-current" size={18} />
                <span className="text-red-700 font-medium">Record Video</span>
              </button>
              
              {/* Video Upload */}
              <button
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.accept = "video/*";
                    fileInputRef.current.capture = "environment";
                    fileInputRef.current.click();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors border border-green-200 text-sm"
              >
                <Video className="text-green-600" size={18} />
                <span className="text-green-700 font-medium">Upload Video</span>
              </button>
            </div>
            
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
          </div>
        ) : showVideoOptions ? (
          <div className="bg-white rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-medium text-gray-800">Record Live Video</h3>
              <button
                onClick={() => setShowVideoOptions(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-48 object-cover rounded-lg bg-black"
                />
                
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center space-x-3">
                  {!isRecording ? (
                    <Button
                      onClick={startLiveRecording}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-full p-3"
                    >
                      <Circle className="fill-current" size={20} />
                    </Button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <div className="bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                        {formatRecordingTime(recordingTime)}
                      </div>
                      <Button
                        onClick={stopRecording}
                        className="bg-gray-600 hover:bg-gray-700 text-white rounded-full p-3"
                      >
                        <Square size={20} />
                      </Button>
                    </div>
                  )}
                </div>
                
                {isRecording && (
                  <div className="absolute top-3 left-3 flex items-center space-x-1">
                    <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                    <span className="text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
                      Recording
                    </span>
                  </div>
                )}
              </div>
              
              <div className="text-center text-xs text-gray-600 space-y-1">
                <p>📱 Hold horizontally for better quality</p>
                {isLiteMode && (
                  <p className="text-orange-600 font-medium">
                    🚀 Auto-stops at {videoLimits.maxDurationSeconds}s for fast 2G/3G uploads
                  </p>
                )}
                <p>🌾 Show your crops or farming progress</p>
              </div>
            </div>
          </div>
        ) : isUploading ? (
          <div className="bg-white rounded-xl p-4 text-center border border-gray-200">
            <div className="flex flex-col items-center space-y-3">
              <Upload className="text-blue-600 animate-pulse" size={32} />
              <div className="w-full max-w-xs">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-2">Uploading... {uploadProgress}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  📁 Organizing in structured folders by user and date
                </p>
              </div>
            </div>
          </div>
        ) : selectedMedia ? (
          <div className="space-y-4">
            <div className="relative">
              {mediaType === 'video' ? (
                <video
                  src={selectedMedia}
                  className="w-full h-48 object-cover rounded-xl"
                  controls
                  muted
                />
              ) : (
                <img
                  src={selectedMedia}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-xl"
                />
              )}
              <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2">
                {mediaType === 'video' ? <Video size={16} /> : <Upload size={16} />}
              </div>
              <button
                onClick={() => {
                  setSelectedMedia(null);
                  setMediaType(null);
                }}
                className="absolute top-2 left-2 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 w-6 h-6 flex items-center justify-center text-sm"
              >
                ×
              </button>
            </div>

            {/* Media Title Input - NEW */}
            <div className="bg-white rounded-xl p-4 space-y-3">
              <h3 className="font-medium text-gray-800">
                {mediaType === 'video' ? 'Video Title (optional)' : 'Image Title (optional)'}
              </h3>
              <Input
                placeholder={`Give your ${mediaType} a catchy title...`}
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                disabled={isUploading || isPosting || isRecording}
                maxLength={100}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>📹 This title will help organize your video</span>
                <span>{videoTitle.length}/100</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Post Content Section */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-gray-800">What do you want to share? *</h3>
          <MentionTextArea
            value={content}
            onChange={setContent}
            placeholder="Tell your farming community what's happening... Share your progress, challenges, or insights! Use @username to mention other farmers."
            className="min-h-[100px] resize-none"
            disabled={isUploading || isPosting || isRecording}
            maxLength={500}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>💡 Type @ to mention other farmers in your post</span>
            <span>{content.length}/500</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-gray-800">What is this video about? *</h3>
          <RadioGroup value={videoType} onValueChange={setVideoType} className="grid grid-cols-1 gap-3">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="crop-update" id="crop-update" />
              <label htmlFor="crop-update" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                Update on my crop
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="general-commentary" id="general-commentary" />
              <label htmlFor="general-commentary" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                General Commentary
              </label>
            </div>
          </RadioGroup>

          {videoType === "crop-update" && currentYearCrops.length > 0 && (
            <div className="mt-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Select from your crop plan</h4>
              <Select value={selectedCropPlan} onValueChange={setSelectedCropPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a crop from your plan..." />
                </SelectTrigger>
                <SelectContent>
                  {currentYearCrops.map((crop) => (
                    <SelectItem key={crop.id} value={crop.id}>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{crop.commodity}</span>
                        {crop.variety && crop.variety !== "N/A" && (
                          <span className="text-sm text-gray-500">({crop.variety})</span>
                        )}
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {crop.season} {crop.year}
                        </span>
                        {crop.is_primary && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            ★ Primary
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCropPlan && (
                <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                  ✓ This post will be tagged with your selected crop from your plan
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-3 space-y-2">
          <h3 className="font-medium text-gray-800 text-sm">How are things going? *</h3>
          
          <RadioGroup value={sentiment} onValueChange={setSentiment} className="grid grid-cols-2 gap-2">
            {sentimentOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <div key={option.id} className="flex items-center">
                  <RadioGroupItem value={option.id} id={option.id} className="sr-only" />
                  <label 
                    htmlFor={option.id}
                    className={`flex items-center justify-center space-x-2 p-2 rounded-lg border cursor-pointer transition-all w-full relative ${
                      sentiment === option.id 
                        ? `${option.selectedBgColor} ${option.selectedBorderColor} ${option.color} shadow-md` 
                        : `${option.bgColor} ${option.borderColor} ${option.color} ${option.hoverBgColor}`
                    }`}
                  >
                    <IconComponent size={16} />
                    <span className="text-sm font-medium">{option.label}</span>
                    {sentiment === option.id && (
                      <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                        <div className="w-2 h-2 bg-current rounded-full"></div>
                      </div>
                    )}
                  </label>
                </div>
              );
            })}
          </RadioGroup>
        </div>

        <div className="bg-white rounded-xl p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">
              Challenges (optional) - Select all that apply
            </label>
            
            <div className="grid grid-cols-2 gap-2 mb-4">
              {predefinedChallenges.map((challenge) => (
                <div key={challenge.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={challenge.id}
                    checked={selectedChallenges.includes(challenge.id)}
                    onCheckedChange={() => handleChallengeToggle(challenge.id)}
                    disabled={isUploading || isPosting || isRecording}
                  />
                  <label 
                    htmlFor={challenge.id} 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {challenge.label}
                  </label>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex space-x-2">
                <Input
                  placeholder="Add your own challenge..."
                  value={newCustomChallenge}
                  onChange={(e) => setNewCustomChallenge(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomChallenge();
                    }
                  }}
                  className="flex-1"
                  disabled={isUploading || isPosting || isRecording}
                />
                <Button
                  type="button"
                  onClick={handleAddCustomChallenge}
                  disabled={!newCustomChallenge.trim() || isUploading || isPosting || isRecording}
                  variant="outline"
                  size="sm"
                >
                  <Plus size={16} />
                </Button>
              </div>

              {customChallenges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {customChallenges.map((challenge, index) => (
                    <div
                      key={index}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center space-x-2 text-sm"
                    >
                      <span>{challenge}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomChallenge(challenge)}
                        disabled={isUploading || isPosting || isRecording}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(selectedChallenges.length > 0 || customChallenges.length > 0) && (
              <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                <strong>Selected challenges:</strong> {getAllChallenges().join(", ")}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-gray-800">Location (optional)</h3>
          <Input
            placeholder="Enter location (e.g., California, USA)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={isUploading || isPosting || isRecording}
          />
        </div>
      </div>
    </div>
  );
};
